"use client";
import React, { useEffect, useState, useCallback, Fragment } from "react";

import { usefetchDataFromApi } from "@/utils/api";
import BookingCard from "@/components/BookingCard/BookingCard";
import SkeletonBookingCard from "@/components/BookingCard/SkeletonBookingCard";
import dayjs from "dayjs";
import Link from "next/link";

const BookingList = () => {
  const [loading, setLoading] = useState(true);
  const [bookingList, setBookingList] = useState([]);

  const handleBookingList = useCallback(() => {
    setLoading(true);
    usefetchDataFromApi({
      // includePending=1 opts into live pending bookings (payment pending /
      // initiated) with a Complete-payment CTA — old clients never send it
      // and keep today's confirmed/cancelled-only list.
      url: "user/bookings?includePending=1",
      method: "POST",

      data: {
        platform: 3,
      },
    })
      .then((res) => {
        if (res.success) {
          const bookings = res.data?.data?.items || res.data?.data || [];
          const isPending = (b) => b.status == 0 || b.status == 3;
          let sorted = bookings.sort(function (a, b) {
            // Live pending bookings stay pinned on top (they carry the pay
            // CTA); within each group, newest trip first.
            if (isPending(a) !== isPending(b)) {
              return isPending(a) ? -1 : 1;
            }
            return (
              new Date(dayjs(b.start).format("YYYY-MM-DD")) -
              new Date(dayjs(a.start).format("YYYY-MM-DD"))
            );
          });
          setBookingList(sorted);
        }
      })
      .catch(() => {
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    // Data-fetch on mount — setState in .then/.finally is async, not synchronous
    // eslint-disable-next-line react-hooks/set-state-in-effect
    handleBookingList();
  }, [handleBookingList]);
  return (
    <main className="w-full bg-bgGray50 min-h-screen">
        <div className="mx-[5%] lg:mx-[216px]">
          <div className="journey-topbar lg:hidden fixed top-[14px] left-0 w-full h-14 flex items-center gap-2.5 px-5 bg-white/85 backdrop-blur-[20px] z-20">
            <Link href="/user/dashboard" className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-[background] duration-200 hover:bg-black/5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M15 19l-7-7 7-7" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <h2 className="text-[15px] font-semibold tracking-[-0.2px] text-heading">Your Bookings</h2>
          </div>
          <div className="journey-head hidden lg:flex items-start mt-20 mb-6">
            <Link href="/user/dashboard" className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-[background] duration-200 hover:bg-black/5 mt-0.5 mr-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M15 19l-7-7 7-7" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <div>
              <h1 className="text-lg font-semibold tracking-[-0.2px] text-heading">Your Bookings</h1>
              <p className="text-[13px] text-textMuted flex items-center gap-1.5 mt-0.5">
                <Link href="/user/dashboard" className="hover:text-heading transition-colors">Account</Link>
                <span className="text-textDisabled">/</span>
                <span className="text-heading">Bookings</span>
              </p>
            </div>
          </div>

          <div className="journey-body mt-20 lg:mt-0">
            {loading && <SkeletonBookingCard counts={8} />}
            {!loading && bookingList.length == 0 && (
              <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
                <div className="w-16 h-16 rounded-card-lg bg-bgGray100 flex items-center justify-center mb-5">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-[15px] font-semibold text-heading tracking-[-0.2px] mb-1">No bookings yet</h3>
                <p className="text-[13px] text-textMuted">Your booking history will appear here</p>
              </div>
            )}
            {bookingList?.map((orders, i) => {
              return (
                <Fragment key={orders.id || i}>
                  <BookingCard orders={orders} />
                </Fragment>
              );
            })}
          </div>
        </div>
    </main>
  );
};

export default BookingList;
